/**
 * Hidabrut WebView mode. The Hidabrut app opens Kol Torah in a WebView and injects
 * `window.hidabrotPayload` (a Hidabrut user + one of its profiles, like Netflix
 * profiles) instead of a login. Each profile is its own Kol Torah user.
 *
 * Kept free of Firebase imports: App.tsx uses it to decide which bundle to load.
 * The Firebase session lives in hidabrutSession.ts.
 */

export interface HidabrutPayload {
  profileName: string;
  email: string;
  hidabrutProfileId: string;
  hidabrotUserId: string;
  hidabrotToken: string;
}

declare global {
  interface Window {
    hidabrotPayload?: Partial<HidabrutPayload>;
  }
}

// Must match createHidabrutSession (serverless/functions/src/index.ts).
const HIDABRUT_ID_RE = /^[A-Za-z0-9_-]{1,60}$/;
const HIDABRUT_EMAIL_DOMAIN = 'hidabrut.invalid';

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** The injected payload, or null when not running inside Hidabrut (or it's malformed). */
export function getHidabrutPayload(): HidabrutPayload | null {
  if (typeof window === 'undefined') return null;
  const raw = window.hidabrotPayload;
  if (!raw || typeof raw !== 'object') return null;
  const hidabrotUserId = asTrimmedString(raw.hidabrotUserId);
  const hidabrutProfileId = asTrimmedString(raw.hidabrutProfileId);
  if (!HIDABRUT_ID_RE.test(hidabrotUserId) || !HIDABRUT_ID_RE.test(hidabrutProfileId)) {
    return null;
  }
  return {
    hidabrotUserId,
    hidabrutProfileId,
    profileName: asTrimmedString(raw.profileName),
    email: asTrimmedString(raw.email),
    hidabrotToken: asTrimmedString(raw.hidabrotToken),
  };
}

export function isHidabrut(): boolean {
  return getHidabrutPayload() !== null;
}

// Hidabrut opens us with "?hidabrut=true" before (or without) injecting the payload.
// The flag survives in-app navigation (the query string doesn't) for this WebView session.
const HIDABRUT_MODE_KEY = 'hidabrutMode';

export function markHidabrutMode(): void {
  try {
    sessionStorage.setItem(HIDABRUT_MODE_KEY, 'true');
  } catch (e) {
    console.error('[Hidabrut] Failed to persist hidabrut mode:', e);
  }
}

/** Opened from Hidabrut: the payload is present, or the "?hidabrut=true" flag was seen. */
export function isHidabrutMode(): boolean {
  if (isHidabrut()) return true;
  if (typeof window === 'undefined') return false;
  try {
    return (
      sessionStorage.getItem(HIDABRUT_MODE_KEY) === 'true' ||
      new URLSearchParams(window.location.search).get('hidabrut') === 'true'
    );
  } catch {
    return false;
  }
}

/** Firebase uid of a Hidabrut profile. */
export function getHidabrutUid(payload: HidabrutPayload): string {
  return `hid_${payload.hidabrotUserId}_${payload.hidabrutProfileId}`;
}

/** Synthetic per-profile email (profiles share the real Hidabrut email). */
export function getHidabrutProfileEmail(payload: HidabrutPayload): string {
  return `${getHidabrutUid(payload).toLowerCase()}@${HIDABRUT_EMAIL_DOMAIN}`;
}

/**
 * Debug aid: the Hidabrut WebView isn't inspectable, so report what we received to the
 * console (it lands in `adb logcat` under the `chromium` tag). The token is never logged,
 * only whether it is present. Also re-checks after load, in case Hidabrut injects late.
 */
export function logHidabrutPayload(): void {
  if (typeof window === 'undefined') return;
  const report = (when: string) => {
    const raw = window.hidabrotPayload;
    const payload = getHidabrutPayload();
    console.log(
      `[Hidabrut] ${when}: ${payload ? 'payload OK' : raw ? 'payload INVALID' : 'no payload'}`,
      JSON.stringify({
        rawKeys: raw && typeof raw === 'object' ? Object.keys(raw) : null,
        hidabrotUserId: payload?.hidabrotUserId ?? asTrimmedString(raw?.hidabrotUserId),
        hidabrutProfileId: payload?.hidabrutProfileId ?? asTrimmedString(raw?.hidabrutProfileId),
        profileName: asTrimmedString(raw?.profileName),
        email: asTrimmedString(raw?.email),
        hasToken: asTrimmedString(raw?.hidabrotToken).length > 0,
        uid: payload ? getHidabrutUid(payload) : null,
        href: window.location.href,
      }),
    );
  };
  report('startup');
  window.addEventListener('load', () => report('window.load'));
  setTimeout(() => report('after 3s'), 3000);
}

// --- Profile switching -------------------------------------------------------
// Profiles share the WebView's localStorage, and progress/selections live only in
// localStorage. When the active profile changes, the previous profile's data is
// stashed under its uid and the new profile's stash (if any) is restored.

const ACTIVE_UID_KEY = 'hidabrutActiveUid';
const STASH_PREFIX = 'hidabrutProfileStash:';
// Device-level keys that are shared by all profiles and never stashed.
const DEVICE_KEYS = new Set([ACTIVE_UID_KEY, 'i18nextLng', 'react_id', 'app', 'showPayments']);

function isDeviceKey(key: string): boolean {
  return DEVICE_KEYS.has(key) || key.startsWith(STASH_PREFIX) || key.startsWith('mp_');
}

function profileKeys(): string[] {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key !== null && !isDeviceKey(key)) keys.push(key);
  }
  return keys;
}

/**
 * `loggedInUser` for the profile. Written synchronously at startup so every page sees
 * the user right away (the Firebase session is created asynchronously afterwards).
 */
export function writeHidabrutLoggedInUser(payload: HidabrutPayload): void {
  const uid = getHidabrutUid(payload);
  const name = payload.profileName || undefined;
  localStorage.setItem(
    'loggedInUser',
    JSON.stringify({
      userId: uid,
      uid,
      email: getHidabrutProfileEmail(payload),
      name,
      displayName: name,
    }),
  );
}

/**
 * Call once at startup, before anything reads localStorage. Swaps the profile-specific
 * localStorage when Hidabrut opened us with a different profile than last time, then
 * sets `loggedInUser` for the current profile. Returns true when the profile changed.
 */
export function prepareHidabrutProfileStorage(): boolean {
  const payload = getHidabrutPayload();
  if (!payload) return false;
  const changed = swapProfileStorage(payload);
  try {
    writeHidabrutLoggedInUser(payload);
  } catch (e) {
    console.error('[Hidabrut] Failed to set loggedInUser:', e);
  }
  return changed;
}

function swapProfileStorage(payload: HidabrutPayload): boolean {
  try {
    const uid = getHidabrutUid(payload);
    const activeUid = localStorage.getItem(ACTIVE_UID_KEY);
    if (activeUid === uid) return false;

    const keys = profileKeys();
    if (activeUid) {
      const stash: Record<string, string> = {};
      keys.forEach((key) => {
        const value = localStorage.getItem(key);
        if (value !== null) stash[key] = value;
      });
      try {
        localStorage.setItem(STASH_PREFIX + activeUid, JSON.stringify(stash));
      } catch (e) {
        console.error('[Hidabrut] Failed to stash previous profile data:', e);
      }
    }
    keys.forEach((key) => localStorage.removeItem(key));

    const saved = localStorage.getItem(STASH_PREFIX + uid);
    if (saved) {
      const restored = JSON.parse(saved) as Record<string, string>;
      Object.entries(restored).forEach(([key, value]) => localStorage.setItem(key, value));
      localStorage.removeItem(STASH_PREFIX + uid);
    }
    localStorage.setItem(ACTIVE_UID_KEY, uid);
    return true;
  } catch (e) {
    console.error('[Hidabrut] Profile storage switch failed:', e);
    return false;
  }
}
